import { useRef, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, splitList } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { btn, TextField, TextArea, SelectField, Check, ErrorBox, DevLink } from "../components/kit";

export const ROLE_OPTS = [
  ["job_seeker", "Job Seeker", "Search and apply for jobs"],
  ["freelancer", "Freelancer", "Find projects and send proposals"],
  ["employer", "Employer / Recruiter", "Post jobs and manage applicants"],
  ["client", "Client", "Post freelance projects and review proposals"],
  ["mentor", "Mentor / Professional", "Offer guidance in the professional directory"],
];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState(1);
  const [err, setErr] = useState("");
  const [roles, setRoles] = useState(user.roles?.filter((r) => r !== "admin").length ? user.roles : [sp.get("role") || "job_seeker"]);
  const p0 = user.profile || {};
  const [p, setP] = useState({ headline: p0.headline || "", location: p0.location || "", remote_pref: p0.remote_pref || "", skills: (p0.skills || []).join(", "), interests: (p0.interests || []).join(", "), education: p0.education || "", experience: p0.experience || "" });
  const [note, setNote] = useState(user.accessibility_note || "");
  const heading = useRef(null);
  useEffect(() => { heading.current?.focus(); }, [step]);
  const on = (k) => (e) => setP({ ...p, [k]: e.target.value });

  const next = async () => {
    setErr("");
    try {
      if (step === 1) {
        if (!roles.length) { setErr("Choose at least one role to continue."); return; }
        const { data } = await api.put("/me/roles", { roles }); setUser(data);
      } else {
        const { data } = await api.put("/me/profile", { profile: { ...p, skills: splitList(p.skills), interests: splitList(p.interests) }, ...(step === 4 ? { accessibility_note: note } : {}) });
        setUser(data);
      }
      if (step < 4) setStep(step + 1); else { toast.success("Profile saved. Welcome to OpenPath!"); nav("/dashboard"); }
    } catch (e) { setErr(errMsg(e)); }
  };
  const titles = ["How will you use OpenPath?", "About you", "Skills and experience", "Accessibility preferences (optional)"];
  return (
    <div className="container-x py-12 max-w-2xl">
      <p className="font-semibold text-sub" data-testid="onboarding-step-indicator">Step {step} of 4</p>
      <div className="mt-2 h-2 rounded-full bg-mist" aria-hidden="true"><div className="h-2 rounded-full bg-blue" style={{ width: `${step * 25}%` }} /></div>
      <h1 ref={heading} tabIndex={-1} className="mt-6 text-3xl md:text-4xl font-extrabold outline-none">{titles[step - 1]}</h1>
      <DevLink link={sessionStorage.getItem("aptiport-dev-verify")} />
      <div className="mt-8 space-y-5">
        {step === 1 && (
          <fieldset className="space-y-3" data-testid="onboarding-roles">
            <legend className="mb-2 text-sub">Choose all that apply. You can change this later in settings.</legend>
            {ROLE_OPTS.map(([v, l, h]) => <div key={v} className="card px-4 py-2"><Check id={`role-${v}`} label={l} hint={h} checked={roles.includes(v)} onChange={(e) => setRoles(e.target.checked ? [...roles, v] : roles.filter((r) => r !== v))} /></div>)}
          </fieldset>
        )}
        {step === 2 && <>
          <TextField id="ob-headline" label="Professional headline" hint="For example: Accounts assistant with 3 years' experience" value={p.headline} onChange={on("headline")} />
          <TextField id="ob-location" label="Preferred location" autoComplete="address-level2" value={p.location} onChange={on("location")} />
          <SelectField id="ob-remote" label="Work arrangement preference" value={p.remote_pref} onChange={on("remote_pref")} placeholder="No preference" options={[["remote", "Remote"], ["hybrid", "Hybrid"], ["onsite", "On-site"]]} />
        </>}
        {step === 3 && <>
          <TextField id="ob-skills" label="Skills" hint="Separate with commas, e.g. Excel, Customer service, Python. Used for transparent job matching." value={p.skills} onChange={on("skills")} />
          <TextField id="ob-interests" label="Career interests" hint="Separate with commas" value={p.interests} onChange={on("interests")} />
          <TextArea id="ob-education" label="Education" rows={3} value={p.education} onChange={on("education")} />
          <TextArea id="ob-experience" label="Experience" rows={3} value={p.experience} onChange={on("experience")} />
        </>}
        {step === 4 && <>
          <p className="text-ink">
            Sharing anything here is entirely voluntary. You never need to disclose a disability to use
            OpenPath. This note is private: it is never shown on your public profile and is only shared if
            you choose to include accommodation needs in a specific application.
          </p>
          <TextArea id="ob-a11y-note" label="Accommodation or accessibility notes for yourself" hint="For example, interview formats that work best for you." rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
          <p className="text-sm text-sub">You can also adjust text size, contrast and motion using the Accessibility button in the header.</p>
        </>}
        {err && <ErrorBox message={err} />}
        <div className="flex flex-wrap justify-between gap-3 pt-4">
          {step > 1 ? <button className={btn("secondary")} onClick={() => setStep(step - 1)} data-testid="onboarding-back-btn">Back</button> : <span />}
          <div className="flex gap-3">
            {step > 1 && <button className={btn("ghost")} onClick={() => (step < 4 ? setStep(step + 1) : nav("/dashboard"))} data-testid="onboarding-skip-btn">Skip this step</button>}
            <button className={btn()} onClick={next} data-testid="onboarding-next-btn">{step < 4 ? "Save and continue" : "Finish"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
